import {
  RecordRepresentation,
  useRecordContext,
  useTranslate,
  useUpdate,
} from "ra-core";
import { EditButton } from "@/components/admin/edit-button";
import { DeleteButton } from "@/components/admin";
import { ReferenceField } from "@/components/admin/reference-field";
import { TextField } from "@/components/admin/text-field";
import { Card, CardContent } from "@/components/ui/card";

import { AsideSection } from "../misc/AsideSection";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { CustomFieldsDisplay } from "../misc/CustomFields";
import type { Contact } from "../types";
import { Avatar } from "./Avatar";
import { ContactBackgroundInfo } from "./ContactBackgroundInfo";
import { LeadScoreBadge, useLeadScore } from "./leadScore";
import { EmailComposeSheet } from "./EmailComposeSheet";
import { Button } from "@/components/ui/button";
import { Mail } from "lucide-react";
import { useState } from "react";
import { ContactPersonalInfo } from "./ContactPersonalInfo";
import { ContactStatusSelector } from "./ContactInputs";
import { ExportVCardButton } from "./ExportVCardButton";
import { TagsListEdit } from "./TagsListEdit";

/**
 * Left "about" rail of the contact record - identity, properties, and
 * actions, HubSpot-style. Associations live in the right column; the
 * timeline owns the center.
 */

/** Compose an email to this contact - logs to the timeline on send. */
const EmailButton = () => {
  const record = useRecordContext<Contact>();
  const [open, setOpen] = useState(false);
  if (!record) return null;
  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="h-8"
        onClick={() => setOpen(true)}
      >
        <Mail className="w-3.5 h-3.5" /> Email
      </Button>
      <EmailComposeSheet contact={record} open={open} onOpenChange={setOpen} />
    </>
  );
};

const ScoreChip = () => {
  const record = useRecordContext<Contact>();
  const score = useLeadScore(record);
  if (!score) return null;
  return <LeadScoreBadge score={score} />;
};

/** Funnel-position selector: chips up the tenant's lifecycle ladder. */
const LifecycleStageSelector = () => {
  const record = useRecordContext<Contact>();
  const { lifecycleStages } = useConfigurationContext();
  const [update] = useUpdate();
  if (!record) return null;
  const currentIdx = lifecycleStages.findIndex(
    (s) => s.value === record.lifecycle_stage,
  );
  return (
    <div className="flex flex-wrap gap-1">
      {lifecycleStages.map((s, i) => (
        <button
          key={s.value}
          type="button"
          title={`Set lifecycle stage to ${s.label}`}
          onClick={() =>
            update("contacts", {
              id: record.id,
              data: { lifecycle_stage: s.value },
              previousData: record,
            })
          }
          className={`rounded-full border px-2 py-0.5 text-[11px] transition-colors ${
            i === currentIdx
              ? "border-primary bg-primary/10 font-semibold"
              : i < currentIdx
                ? "border-border text-muted-foreground bg-muted/60"
                : "border-border text-muted-foreground hover:border-primary/50"
          }`}
        >
          {s.label}
        </button>
      ))}
    </div>
  );
};
export const ContactAboutRail = () => {
  const record = useRecordContext<Contact>();
  const translate = useTranslate();
  if (!record) return null;

  return (
    <div className="hidden lg:flex flex-col gap-4 text-sm">
      <Card>
        <CardContent className="px-4 flex flex-col gap-3">
          <div className="flex items-center gap-3">
            <Avatar />
            <div className="min-w-0">
              <h1 className="text-lg font-semibold leading-tight truncate flex items-center gap-2">
                <RecordRepresentation />
                <ScoreChip />
              </h1>
              <p className="text-xs text-muted-foreground truncate">
                {record.title}
                {record.title && record.company_id != null ? " at " : ""}
                {record.company_id != null && (
                  <ReferenceField
                    source="company_id"
                    reference="companies"
                    link="show"
                  >
                    <TextField source="name" className="underline" />
                  </ReferenceField>
                )}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-1 -ml-1">
            <EmailButton />
            <EditButton label="resources.contacts.action.edit" />
            <ExportVCardButton />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="px-4">
          <AsideSection title={translate("resources.notes.fields.status")}>
            <ContactStatusSelector />
          </AsideSection>

          <AsideSection title="Lifecycle stage">
            <LifecycleStageSelector />
          </AsideSection>

          <AsideSection
            title={translate(
              "resources.contacts.field_categories.personal_info",
            )}
          >
            <ContactPersonalInfo />
          </AsideSection>

          <AsideSection
            title={translate(
              "resources.contacts.field_categories.background_info",
            )}
          >
            <ContactBackgroundInfo />
          </AsideSection>

          <AsideSection
            title={translate("resources.tags.name", { smart_count: 2 })}
          >
            <TagsListEdit />
          </AsideSection>

          <CustomFieldsDisplay object="contacts" />

          <div className="mt-4 pt-4 border-t flex flex-col gap-2 items-start">
            <DeleteButton
              className="h-6 cursor-pointer hover:bg-destructive/10! text-destructive! border-destructive! focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40"
              size="sm"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
